import { forwardRef } from 'react'
import { BookmarkAdded } from '@mui/icons-material'
import { Stack, Tooltip } from '@mui/material'
import {
  TreeItemLabel,
  useTreeItem,
  TreeItemProvider,
  TreeItemIconContainer,
  TreeItemIcon,
  TreeItemProps,
  TreeItemContent,
  TreeItemCheckbox,
  TreeItemGroupTransition,
  TreeItemLabelInput,
  TreeItemRoot,
  useTreeItemModel,
  useTreeItemUtils,
} from '@mui/x-tree-view-pro'

const CustomTreeItem = forwardRef(function CustomTreeItem(
  { id, itemId, label, disabled, children }: TreeItemProps,
  ref: React.Ref<HTMLLIElement>,
) {
  const {
    getContextProviderProps,
    getRootProps,
    getContentProps,
    getLabelProps,
    getGroupTransitionProps,
    getIconContainerProps,
    getLabelInputProps,
    getCheckboxProps,
    status,
    publicAPI,
  } = useTreeItem({ id, itemId, label, disabled, children, rootRef: ref })

  const { interactions } = useTreeItemUtils({ itemId, children })

  const item = useTreeItemModel<{ alreadyAdded?: boolean; partiallyAdded?: boolean }>(itemId)

  return (
    <TreeItemProvider {...getContextProviderProps()}>
      <TreeItemRoot {...getRootProps()}>
        <TreeItemContent {...getContentProps()}>
          <TreeItemIconContainer {...getIconContainerProps()}>
            <TreeItemIcon status={status} />
          </TreeItemIconContainer>

          <TreeItemCheckbox {...getCheckboxProps()} />

          {status.editing ? (
            <TreeItemLabelInput {...getLabelInputProps()} />
          ) : (
            <TreeItemLabel
              {...getLabelProps({
                // Clicking the label toggles the selection like the checkbox does,
                // shift-clicking selects the range from the last clicked item
                onClick: (event: React.MouseEvent) => {
                  if (event.shiftKey) {
                    interactions.handleSelection(event)
                    return
                  }
                  publicAPI.setItemSelection({
                    event,
                    itemId,
                    keepExistingSelection: true,
                    shouldBeSelected: !status.selected,
                  })
                },
              })}
            />
          )}

          {item?.alreadyAdded && (
            <Tooltip
              title={
                item.partiallyAdded
                  ? 'Bereits hinzugefügt, aber noch nicht alle untergeordneten Templates'
                  : 'Bereits hinzugefügt'
              }
            >
              <Stack direction="row">
                <BookmarkAdded color={item.partiallyAdded ? 'warning' : 'success'} />
              </Stack>
            </Tooltip>
          )}
        </TreeItemContent>
        {children && <TreeItemGroupTransition {...getGroupTransitionProps()} />}
      </TreeItemRoot>
    </TreeItemProvider>
  )
})

export default CustomTreeItem
